import React from 'react';
import { Instagram, ExternalLink } from 'lucide-react';
import { useSiteSettings } from '../hooks/useSiteData';

// Default gallery, used until posts are added in Admin → Social & Payments → Instagram section.
const POSTS = [
  {
    id: '1',
    image: 'https://images.unsplash.com/photo-1509785307050-d4066910ec1e?w=600&q=70&auto=format&fit=crop&auto=format&fit=crop',
    caption: 'A fresh drop of small-batch Arabica from Chikmagalur.',
    permalink: null,
  },
  {
    id: '2',
    image: 'https://images.unsplash.com/photo-1571934811356-5cc061b6821f?w=600&q=70&auto=format&fit=crop&auto=format&fit=crop',
    caption: 'Handpicked Nilgiri greens, gently withered.',
    permalink: null,
  },
  {
    id: '3',
    image: 'https://images.unsplash.com/photo-1641291361624-38b69b86b1cf?w=600&q=70&auto=format&fit=crop&auto=format&fit=crop',
    caption: 'Sun-ripened Alphonso, slow-dried to preserve the season.',
    permalink: null,
  },
  {
    id: '4',
    image: 'https://images.unsplash.com/photo-1541469406036-71229832e06e?w=600&q=70&auto=format&fit=crop&auto=format&fit=crop',
    caption: 'Filter kaapi mornings, the old-school way.',
    permalink: null,
  },
  {
    id: '5',
    image: 'https://images.unsplash.com/photo-1567922045116-2a00fae2ed03?w=600&q=70&auto=format&fit=crop&auto=format&fit=crop',
    caption: 'Cardamom-heavy chai for a cool Coorg morning.',
    permalink: null,
  },
  {
    id: '6',
    image: 'https://images.unsplash.com/photo-1598799170815-933217492614?w=600&q=70&auto=format&fit=crop&auto=format&fit=crop',
    caption: 'Dehydrated tomato flakes — pasta night, sorted.',
    permalink: null,
  },
];

export default function InstagramFeed({ handle: defaultHandle = 'rarelocals' }) {
  const settings = useSiteSettings();
  const ig = settings?.instagram || {};
  if (settings && ig.enabled === false) return null;
  const handle = ig.handle || defaultHandle;
  const tagline = ig.tagline || 'Fresh drops, farm visits and the small moments behind every pack. Come say hi.';
  const posts = ig.posts?.length ? ig.posts.map((p, i) => ({ id: String(i), image: p.image, caption: p.caption || `@${handle} on Instagram`, permalink: p.permalink || null })) : POSTS;
  return (
    <section aria-labelledby="ig-heading" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16" data-testid="instagram-feed">
      <div className="text-center mb-8 sm:mb-10">
        <div className="text-[10px] sm:text-xs uppercase tracking-[0.3em] text-brand-gold mb-2 flex items-center justify-center gap-2">
          <Instagram size={14} aria-hidden="true" /> Follow us
        </div>
        <h2 id="ig-heading" className="font-serif-brand text-3xl sm:text-4xl text-brand-dark section-heading" data-testid="instagram-handle">
          @{handle}
        </h2>
        <p className="mt-3 text-sm text-brand-green/80 max-w-xl mx-auto" data-testid="instagram-tagline">
          {tagline}
        </p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3">
        {posts.map((p) => {
          const Tag = p.permalink ? 'a' : 'div';
          const props = p.permalink
            ? { href: p.permalink, target: '_blank', rel: 'noopener noreferrer', 'aria-label': `View post: ${p.caption}` }
            : { role: 'img', 'aria-label': p.caption };
          return (
            <Tag
              key={p.id}
              {...props}
              className="group relative aspect-square overflow-hidden rounded-lg bg-brand-ivory block focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold"
            >
              <img
                src={p.image}
                alt={p.caption}
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-brand-dark/0 group-hover:bg-brand-dark/60 transition-colors duration-300 flex items-end p-3">
                <p className="text-[11px] leading-snug text-brand-gold-light opacity-0 group-hover:opacity-100 transition-opacity">
                  {p.caption}
                </p>
              </div>
              {p.permalink && (
                <span className="absolute top-2 right-2 text-brand-gold-light/80 opacity-0 group-hover:opacity-100 transition-opacity">
                  <ExternalLink size={14} aria-hidden="true" />
                </span>
              )}
            </Tag>
          );
        })}
      </div>

      <div className="mt-8 text-center">
        <a
          href={`https://www.instagram.com/${handle}/`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="instagram-view-more"
          className="inline-flex items-center gap-2 text-sm text-brand-green hover:text-brand-gold focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold rounded-md px-2 py-1"
        >
          <Instagram size={16} aria-hidden="true" /> View more on Instagram
        </a>
      </div>
    </section>
  );
}
